import React, { useState, useEffect } from 'react';
import { X, Users } from 'lucide-react';
import { doc, updateDoc, onSnapshot, runTransaction, getDocs, query, collection, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { toast } from 'react-hot-toast';
import { portalDb } from '../utils/portalFirebase';

const ViewCountModal = ({ report: initialReport, onClose, user, resetViewCount }) => {
  const [report, setReport] = useState(initialReport);
  const [connectedClients, setConnectedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientsChecked, setClientsChecked] = useState(false);
  
  // Update local state when prop changes
  useEffect(() => {
    setReport(initialReport);
  }, [initialReport]);

  useEffect(() => {
    if (!initialReport?.id) return;

    // Set up real-time listener for this specific report
    const unsubscribe = onSnapshot(
      doc(db, 'inspections', initialReport.id),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setReport(prev => ({
            ...prev,
            ...data,
            id: doc.id,
            viewCount: data.viewCount || 0,
            viewLog: data.viewLog || [],
            downloadCount: data.downloadCount || 0
          }));
        }
      },
      (error) => {
        console.error('Error in view count listener:', error);
      }
    );

    return () => unsubscribe();
  }, [initialReport?.id]);

  // Fetch connected clients from portal database
  useEffect(() => {
    if (!initialReport?.id) return;

    const fetchConnectedClients = async () => {
      setLoadingClients(true);
      setClientsChecked(false);
      try {
        // Query portal users who have this report in their savedReports array
        const clientsQuery = query(
          collection(portalDb, 'users'),
          where('savedReports', 'array-contains', initialReport.id)
        );
        
        const clientsSnapshot = await getDocs(clientsQuery);
        
        const clientsList = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setConnectedClients(clientsList);
      } catch (error) {
        console.error('Error fetching connected clients:', error);
        toast.error('Failed to load connected clients');
      } finally {
        setLoadingClients(false);
        setClientsChecked(true);
      }
    };

    fetchConnectedClients();
  }, [initialReport?.id]);

  if (!report) return null;

  const handleResetViewCount = async () => {
    if (!user) return;
    try {
      const reportRef = doc(db, 'inspections', report.id);
      
      // Immediately update local state to show cleared data
      const now = new Date().toISOString();
      setReport(prev => ({
        ...prev,
        viewCount: 0,
        viewLog: [],
        downloadCount: 0,
        downloadLog: [],
        updatedAt: now
      }));
      
      // Use transaction to ensure atomic update
      await runTransaction(db, async (transaction) => {
        const reportDoc = await transaction.get(reportRef);
        if (!reportDoc.exists()) {
          throw new Error("Document does not exist!");
        }

        transaction.update(reportRef, {
          viewCount: 0,
          viewLog: [],
          downloadCount: 0,
          downloadLog: [],
          updatedAt: now
        });
      });
      
      toast.success('Counters reset successfully');
      onClose();
    } catch (error) {
      // Revert local state if update fails
      setReport(prev => ({
        ...prev,
        viewCount: report.viewCount,
        viewLog: report.viewLog,
        downloadCount: report.downloadCount,
        downloadLog: report.downloadLog
      }));
      console.error('Error resetting counters:', error);
      toast.error('Failed to reset counters');
    }
  };

  // Format the timestamp to a more concise readable format
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-3 md:p-4 border-b">
          <h3 className="text-lg md:text-xl font-semibold">Report Activity</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-3 md:p-6 overflow-y-auto">
          <div className="text-center mb-6 grid grid-cols-2 gap-4">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-blue-600">{report.viewCount || 0}</div>
              <p className="text-gray-500 mt-2">Total Views</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-green-600">{report.downloadCount || 0}</div>
              <p className="text-gray-500 mt-2">Total Downloads</p>
            </div>
          </div>
          
          {/* Connected Clients Section - Only show when there are connected clients */}
          {clientsChecked && !loadingClients && connectedClients.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <Users className="h-4 w-4 mr-1 text-blue-500" />
                Connected Client Accounts
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-40 overflow-y-auto">
                  <ul className="divide-y divide-gray-200">
                    {connectedClients.map(client => (
                      <li key={client.id} className="py-2 px-3 text-sm bg-blue-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{client.firstName} {client.lastName}</p>
                            <p className="text-xs text-gray-500">{client.email}</p>
                            {client.companyName && (
                              <p className="text-xs text-gray-500">{client.companyName}</p>
                            )}
                          </div>
                          <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            {client.userType || 'Client'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {/* Loading state for clients - only show after a brief delay to prevent flashing */}
          {clientsChecked && loadingClients && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <Users className="h-4 w-4 mr-1 text-blue-500" />
                Connected Client Accounts
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <div className="py-4 px-3 text-sm text-center text-gray-500">
                  Loading connected clients...
                </div>
              </div>
            </div>
          )}
          
          {(report.viewLog?.length > 0 || (report.downloadLog && report.downloadLog.length > 0)) && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Activity Timeline
              </h4>
              <div className="max-h-80 overflow-y-auto border rounded-lg">
                {/* Combine view and download logs into a single timeline */}
                {(() => {
                  // Create combined timeline
                  const timeline = [
                    ...(report.viewLog || []).map(item => ({
                      ...item,
                      type: 'view'
                    })),
                    ...(report.downloadLog || []).map(item => ({
                      ...item,
                      type: 'download'
                    }))
                  ];
                  
                  // Sort by timestamp, most recent first
                  timeline.sort((a, b) => 
                    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                  );
                  
                  if (timeline.length === 0) {
                    return (
                      <div className="py-4 px-3 text-sm text-center text-gray-500">
                        No activity yet
                      </div>
                    );
                  }
                  
                  return timeline.map((item, index) => {
                    // Determine the display text and title text
                    let displayText = 'View';
                    let titleText = 'View';
                    let isDownload = item.type === 'download';
                    let hasUserName = item.source === 'oiltankportal' && item.userName;
                    
                    if (isDownload) {
                      displayText = 'Download';
                      titleText = 'Download';
                    } else if (hasUserName) {
                      displayText = `By: ${item.userName}`;
                      titleText = `Viewed by ${item.userName}`;
                    }
                    
                    // Determine badge classes based on type
                    let badgeClasses = "ml-auto text-xs font-medium px-2 py-0.5 rounded-full truncate ";
                    
                    // Add color classes
                    badgeClasses += isDownload 
                      ? 'bg-green-100 text-green-800 ' 
                      : 'bg-blue-100 text-blue-800 ';
                    
                    // Only add min/max width and text-right for entries with usernames
                    badgeClasses += hasUserName 
                      ? 'min-w-[80px] max-w-[200px] text-right ' 
                      : 'text-center ';
                    
                    return (
                      <div 
                        key={index} 
                        className={`py-2 px-3 border-b last:border-b-0 text-sm ${
                          isDownload ? 'bg-green-50' : 'bg-blue-50'
                        }`}
                      >
                        <div className={`flex items-center ${
                          isDownload ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {isDownload ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                          <span>{formatTimestamp(item.timestamp)}</span>
                          <span 
                            className={badgeClasses}
                            title={titleText}
                          >
                            {displayText}
                          </span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-500 mt-4 text-center">
            Views are counted when someone accesses the report through a shared link. Downloads are tracked when the report is downloaded. Both counters exclude actions by the report creator.
          </p>
          {user && (
            <button
              onClick={resetViewCount ? () => resetViewCount(report.id) : handleResetViewCount}
              className="w-full mt-6 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            >
              Reset All Counters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewCountModal; 